import Handlebars from 'handlebars';

// Fields to exclude from schema generation (non-user-editable fields)
// ID is excluded because it's used internally by ordered-list components to manage items from queries
// System is excluded because it's a global context variable provided by the runtime
const EXCLUDED_FIELDS = ['json', 'Project', 'ID', 'column', 'System'];

interface Block {
  name: string;
  partial: string;
}

type ScalarNodeType = 'string' | 'number' | 'boolean';
type ComplexNodeType = 'object' | 'array';
type SchemaNodeType = ScalarNodeType | ComplexNodeType;

type ScalarSchemaNode = {
  type: ScalarNodeType;
};

type ObjectSchemaNode = {
  type: 'object';
  children: Map<string | null, SchemaNode>;
};

type ArraySchemaNode = {
  type: 'array';
  children: SchemaNode;
};

type SchemaNode = ScalarSchemaNode | ObjectSchemaNode | ArraySchemaNode;

type ScalarJsonSchema = {
  type: ScalarNodeType;
};

type ObjectJsonSchema = {
  type: 'object';
  properties: Record<string, JsonSchema>;
};

type ArrayJsonSchema = {
  type: 'array';
  items: JsonSchema;
};

type JsonSchema = (ArrayJsonSchema | ObjectJsonSchema | ScalarJsonSchema) & {
  inputType?: string;
  inputOptions?: Record<string, any>;
  required?: string[];
};

// interface JsonSchema {
//   type: string;
//   properties?: Record<string, any>;
//   required?: string[];
//   items?:
//     | JsonSchema
//     | {
//         type: string;
//         properties?: Record<string, any>;
//       };
// }

const toCamelCase = (str: string) => {
  return str
    .trim()
    .toLowerCase()
    .split(/[\s_-]+/)
    .map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join('');
};

/**
 * Improved version of handlebarsToJsonSchema that correctly handles various Handlebars statements
 * and only includes relevant fields from pageContext
 *
 * @param entryPointName - The name of the entry point template
 * @param blocks - Array of blocks with name and html properties
 * @returns JSON Schema object representing the structure of the templates
 */
export const handlebarsToJsonSchemaV2 = (entryPointName: string, blocks: Block[], contextKey = '') => {
  // Map of block names to their parsed AST
  const blockAstMap = new Map<string, hbs.AST.Program>();

  // Parse all blocks and store their AST
  for (const block of blocks) {
    try {
      const ast = Handlebars.parse(block.partial);
      blockAstMap.set(block.name, ast);
    } catch (error) {
      console.error(`Error parsing block ${block.name}:`, error);
    }
  }

  // Process the entry point template
  const visitedTemplates = new Set<string>();
  const schemaNode = processTemplate(entryPointName, blockAstMap, visitedTemplates, contextKey);
  if (!schemaNode) {
    return null;
  }

  const schema: JsonSchema = {
    type: 'object',
    properties: {},
  };
  buildSchemaFromUsedFields(entryPointName, schemaNode, schema, contextKey);
  return schema;
};

// Schema node types for tracking field usage

/**
 * Process a template and collect used fields
 */
const processTemplate = (
  templateName: string,
  blockAstMap: Map<string, hbs.AST.Program>,
  visitedTemplates: Set<string>,
  contextKey: string,
) => {
  // Prevent infinite recursion
  if (visitedTemplates.has(templateName)) {
    return;
  }

  visitedTemplates.add(templateName);

  const ast = blockAstMap.get(templateName);
  if (!ast) {
    console.warn(`Template "${templateName}" not found in blocks`);
    return;
  }

  // Process the AST
  return processAst(ast, blockAstMap, visitedTemplates, contextKey);
};

/**
 * Process an AST node and collect used fields
 */
const processAst = (
  ast: hbs.AST.Program,
  blockAstMap: Map<string, hbs.AST.Program>,
  visitedTemplates: Set<string>,
  contextKey: string,
  isArrayItem = false,
): SchemaNode | undefined => {
  const children = new Map<string | null, SchemaNode>();
  const schemaNode: SchemaNode = {
    type: 'object',
    children: children,
  };

  for (const statement of ast.body) {
    switch (statement.type) {
      case 'PartialStatement':
        {
          // Handle partial statements (e.g., {{> partialName}})
          const partialStatement = statement as hbs.AST.PartialStatement;
          const name = partialStatement.name as hbs.AST.PathExpression;

          const ast = blockAstMap.get(name.original);
          if (!ast) {
            console.warn(`Partial "${name.original}" not found in blocks`);
            continue;
          }

          const aliases = new Map<string, string>();

          for (const pair of partialStatement.hash?.pairs || []) {
            if (pair.value.type !== 'PathExpression') {
              continue;
            }

            const expression = pair.value as hbs.AST.PathExpression;
            if (expression.depth !== 0) {
              // @todo nested path
              continue;
            }

            const parts = expression.original.split('.');
            // console.log(parts);
            // aliases.set(pair.key, parts[parts.length - 1]);
            if (parts[0] === 'this') {
              if (parts.length === 1) {
                // @todo
                aliases.set(pair.key, 'this');
              } else {
                // @todo nested path
                aliases.set(pair.key, parts.slice(1).join('.'));
              }
            } else {
              // @todo nested path
              aliases.set(pair.key, parts.join('.'));
            }
          }

          const schemaNode = processAst(ast, blockAstMap, visitedTemplates, name.original, isArrayItem);
          if (!schemaNode) {
            continue;
          }

          if (schemaNode.type === 'object') {
            for (const [key, value] of schemaNode.children) {
              const [ctxKey, baseKey, ...parts] = (key ?? 'this').split('.');

              const alias = aliases.get(baseKey);
              if (alias === undefined) {
                // children.set([ctxKey, baseKey, ...parts].join('.'), value);
                children.set([contextKey || ctxKey, baseKey, ...parts].join('.'), value);
                continue;
              }

              if (alias.startsWith('@')) {
                // skip data (global) variables
                continue;
              }

              if (alias === 'this') {
                // @todo
                children.set(null, value);
              } else {
                // console.log(ctxKey, alias, parts);
                // children.set([ctxKey, alias, ...parts].join('.'), value);
                children.set([contextKey || ctxKey, alias, ...parts].join('.'), value);
              }
            }
          } else {
            console.warn('Unexpected type in partial statement', schemaNode);
          }
        }
        break;

      case 'PartialBlockStatement':
        {
          // // Handle partial block statements (e.g., {{#> partialName}}...{{/partialName}})
          const partialBlockStatement = statement as hbs.AST.PartialBlockStatement;

          const name = partialBlockStatement.name as hbs.AST.PathExpression;

          const ast = blockAstMap.get(name.original);
          if (!ast) {
            console.warn(`Partial "${name.original}" not found in blocks`);
            continue;
          }

          const aliases = new Map<string, string>();

          for (const pair of partialBlockStatement.hash?.pairs || []) {
            if (pair.value.type !== 'PathExpression') {
              continue;
            }

            const expression = pair.value as hbs.AST.PathExpression;
            if (expression.depth !== 0) {
              // @todo nested path
              continue;
            }

            const parts = expression.original.split('.');
            // console.log(parts);
            // aliases.set(pair.key, parts[parts.length - 1]);
            if (parts[0] === 'this') {
              if (parts.length === 1) {
                // @todo
                aliases.set(pair.key, 'this');
              } else {
                // @todo nested path
                aliases.set(pair.key, parts.slice(1).join('.'));
              }
            } else {
              // @todo nested path
              aliases.set(pair.key, parts.join('.'));
            }
          }

          const partialSchemaNode = processAst(ast, blockAstMap, visitedTemplates, name.original, isArrayItem);
          if (!partialSchemaNode) {
            continue;
          }

          if (partialSchemaNode.type === 'object') {
            for (const [key, value] of schemaNode.children) {
              const [ctxKey, baseKey, ...parts] = (key ?? 'this').split('.');
              const alias = aliases.get(baseKey);
              if (alias === undefined) {
                // children.set([ctxKey, baseKey, ...parts].join('.'), value);
                children.set([contextKey || ctxKey, baseKey, ...parts].join('.'), value);
                continue;
              }

              if (alias.startsWith('@')) {
                // skip data (global) variables
                continue;
              }

              if (alias === 'this') {
                // @todo
                children.set(null, value);
              } else {
                // children.set([ctxKey, alias, ...parts].join('.'), value);
                children.set([contextKey || ctxKey, alias, ...parts].join('.'), value);
              }
            }
          } else {
            console.warn('Unexpected type in partial block partial', partialSchemaNode);
          }

          const blockSchemaNode = processAst(
            partialBlockStatement.program,
            blockAstMap,
            visitedTemplates,
            contextKey,
            isArrayItem,
          );
          if (!blockSchemaNode) {
            continue;
          }

          if (blockSchemaNode.type === 'object') {
            for (const [k, v] of blockSchemaNode.children) {
              children.set(k, v);
            }
          } else {
            console.warn('Unexpected type in partial block block', blockSchemaNode);
          }
        }
        break;

      case 'MustacheStatement':
        {
          // Handle mustache statements (e.g., {{variable}} or {{helper variable}})
          const mustacheStatement = statement as hbs.AST.MustacheStatement;

          // Process the path if it's a PathExpression
          if (mustacheStatement.path.type !== 'PathExpression') {
            continue;
          }

          const path = mustacheStatement.path as hbs.AST.PathExpression;

          // Check if this is a helper invocation (has params)
          const isHelperInvocation = mustacheStatement.params && mustacheStatement.params.length > 0;

          if (isHelperInvocation) {
            // This is a helper like {{default contactsTitle 'value'}}
            // Process the params which contain the actual variables
            for (const param of mustacheStatement.params) {
              if (param.type === 'PathExpression') {
                const paramPath = param as hbs.AST.PathExpression;

                if (paramPath.depth !== 0) {
                  // @todo nested path
                  continue;
                }

                if (paramPath.original.startsWith('@')) {
                  // skip data (global) variables
                  continue;
                }

                if (paramPath.original === 'this') {
                  // @todo
                  children.set(null, { type: 'string' });
                } else {
                  // @todo nested path
                  children.set([contextKey, ...paramPath.parts].join('.'), { type: 'string' });
                }
              }
            }
          } else {
            // This is a simple variable like {{variable}}
            if (path.depth !== 0) {
              // @todo nested path
              continue;
            }

            if (path.original.startsWith('@')) {
              // skip data (global) variables
              continue;
            }

            if (path.original === 'this') {
              // @todo
              children.set(null, { type: 'string' });
            } else {
              // @todo nested path
              children.set([contextKey, ...path.parts].join('.'), { type: 'string' });
            }
          }

          // const parts = path.original.split('.');

          // addPathToUsedFields(path.original, usedFields, isArrayItem);
          // Process hash parameters
          // processHashPairs(mustacheStatement.hash?.pairs, usedFields, currentContext);
        }
        break;

      case 'BlockStatement':
        {
          // Handle block statements (e.g., {{#if variable}} or {{#each items}})
          const blockStatement = statement as hbs.AST.BlockStatement;
          const path = blockStatement.path;

          // Process hash parameters
          // Block statement does not have hash params??
          // processHashPairs(blockStatement.hash?.pairs, usedFields, currentContext);

          if (path.original === 'if' || path.original === 'unless') {
            // For if/unless blocks, process the condition
            if (blockStatement.params && blockStatement.params.length > 0) {
              const param = blockStatement.params[0] as hbs.AST.PathExpression;
              if (param && param.type === 'PathExpression') {
                const fieldKey = [contextKey, ...param.parts].join('.');
                // Only add if it doesn't already exist (e.g., from {{#each}})
                // This prevents overwriting array types with boolean
                if (!children.has(fieldKey)) {
                  children.set(fieldKey, {
                    type: 'boolean',
                  });
                }
              }
            }

            // Process the block contents to collect fields inside the if/unless block
            const schemaNode = processAst(blockStatement.program, blockAstMap, visitedTemplates, contextKey, isArrayItem);
            if (schemaNode && schemaNode.type === 'object') {
              // Merge the collected fields into the parent schema
              for (const [k, v] of schemaNode.children) {
                children.set(k, v);
              }
            }

            // Process the else block if it exists
            if (blockStatement.inverse) {
              const inverseSchemaNode = processAst(
                blockStatement.inverse,
                blockAstMap,
                visitedTemplates,
                contextKey,
                isArrayItem,
              );
              if (inverseSchemaNode && inverseSchemaNode.type === 'object') {
                // Merge the fields from the else block as well
                for (const [k, v] of inverseSchemaNode.children) {
                  children.set(k, v);
                }
              }
            }
          } else if (path.original === 'each') {
            // For each blocks, process the array variable
            if (blockStatement.params.length === 0) {
              continue;
            }

            const param = blockStatement.params[0] as hbs.AST.PathExpression;
            if (param.type !== 'PathExpression') {
              continue;
            }

            if (path.depth !== 0) {
              // @todo nested path
              continue;
            }

            // Create a temporary schema for array items

            // Process the block contents with the new context to collect fields used inside the each block
            const schemaNode = processAst(blockStatement.program, blockAstMap, visitedTemplates, contextKey, true);
            if (!schemaNode) {
              continue;
            }

            // @todo nested
            // if (schemaNode.type === 'array') {
            //   // for (const [k, v] of schemaNode.children) {
            //   //   children.set([...param.parts, '0', k].join('.'), {
            //   //     type: v.type,
            //   //   });
            //   // }
            // } else

            if (schemaNode.type === 'object') {
              for (const [k, v] of schemaNode.children) {
                const pathParts = [contextKey, ...param.parts, '0'];
                if (k !== null) {
                  const [ctxKey, ...parts] = k.split('.');
                  pathParts.push(parts.join('.'));
                }

                children.set(pathParts.join('.'), v);
              }
            } else {
              console.warn('Unexpected type in "each" block', schemaNode);
            }

            // else if (schemaNode.type !== 'array') {
            //   children.set([...param.parts, '0'].join('.'), {
            //     type: 'array',
            //     arrayItemType: schemaNode,
            //   });
            // }
          } else {
            // // For other block helpers, add the path to used fields
            // addPathToUsedFields(path.original, isArrayItem);
            // // Process the block contents
            // processAst(blockStatement.program, blockAstMap, visitedTemplates, hashAliases, isArrayItem);
            // // Process the else block if it exists
            // if (blockStatement.inverse) {
            //   processAst(blockStatement.inverse, blockAstMap, visitedTemplates, hashAliases, isArrayItem);
            // }
            continue;
          }
        }
        break;

      default:
        continue;
    }
  }

  if (schemaNode.children && schemaNode.children.get('')) {
    // @todo
    return schemaNode.children.get('');
  } else {
    return schemaNode;
  }
};

/**
 * Build a JSON schema from the used fields map
 */
const buildSchemaFromUsedFields = (
  entryPointName: string,
  schemaNode: SchemaNode,
  // root: ArrayJsonSchema | ObjectJsonSchema,
  root: ObjectJsonSchema,
  contextKey: string,
  required = false,
) => {
  if (schemaNode.type === 'array') {
    // buildSchemaFromUsedFields(schemaNode.children, root, contextKey, required);
    console.warn('Not supported array type');
  } else if (schemaNode.type === 'object') {
    const entries: [string, SchemaNode][] = [];
    for (const [k, v] of schemaNode.children) {
      if (!k) continue;

      if (k.startsWith(contextKey + '.')) {
        entries.push([k.substring(contextKey.length + 1), v]);
      } else {
        entries.push([k, v]);
      }
    }
    entries.sort((a, b) => a[0].length - b[0].length);

    for (const [path, schema] of entries) {
      // Skip empty paths
      if (!path) continue;

      // Skip excluded fields and fields starting with @ (Handlebars built-ins)
      if (path.startsWith('@')) {
        continue;
      }

      // Handle array expressions and dot notation
      const pathParts = parsePath(path);

      // Check if any part of the path is in the excluded fields list
      const hasExcludedField = pathParts.some(part => EXCLUDED_FIELDS.includes(part.key));
      if (hasExcludedField) {
        continue;
      }

      // Start at the root of the schema
      let current = root;

      // Navigate through the path parts to build the nested structure
      for (let i = 0; i < pathParts.length; i++) {
        const { key, isArray } = pathParts[i];

        // Skip empty keys
        if (!key) continue;

        // If we're at the last part of the path, add the schema
        if (i === pathParts.length - 1) {
          if (isArray) {
            // Create an array property with items of the specified type
            current.properties[key] = {
              type: 'array',
              inputType: 'ordered-list',
              items: {
                type: schema.type as ScalarNodeType,
                inputType: 'text',
                required: [],
                inputOptions: {},
              },
              required: [],
              inputOptions: {
                gqlQueryKey: `${toCamelCase(entryPointName)}_${key}`,
              },
            };
          } else {
            // Create a property with the specified type
            const inputType = schema.type === 'boolean' ? 'checkbox' : 'text';
            current.properties[key] = {
              type: schema.type as ScalarNodeType,
              inputType,
              inputOptions: {},
            };
          }
        } else {
          // We're at an intermediate part of the path
          if (!current.properties[key]) {
            // Create the intermediate object or array
            if (isArray) {
              current.properties[key] = {
                type: 'array',
                inputType: 'ordered-list',
                items: {
                  type: 'object',
                  inputType: 'object',
                  properties: {},
                  required: [],
                  inputOptions: {},
                },
                required: [],
                inputOptions: {
                  gqlQueryKey: `${toCamelCase(entryPointName)}_${key}`,
                },
              };

              // Move to the items object for the next iteration
              // We know this exists because we just created it
              const arrayProp = current.properties[key] as ArrayJsonSchema;
              current = arrayProp.items as ObjectJsonSchema;
            } else {
              current.properties[key] = {
                type: 'object',
                inputType: 'object',
                properties: {},
                required: [],
                inputOptions: {},
              };

              // Move to the nested object for the next iteration
              const objProp = current.properties[key];
              current = objProp as ObjectJsonSchema;
            }
          } else {
            // The property already exists, move to it
            const existingProp = current.properties[key];
            if (existingProp.type === 'array') {
              current = existingProp.items as ObjectJsonSchema;
            } else {
              current = existingProp as ObjectJsonSchema;
            }
          }
        }
      }
    }
  } else {
    console.warn('Not supported scalar type');
    // if (root.type === 'object') {
    //   return { type: schemaNode.type as ScalarNodeType, inputType: 'text' };
    // }
    // else if (root.type === 'array') {
    //   root.items = { type: schemaNode.type as ScalarNodeType, inputType: 'text' };
    // }
  }

  /**
   * Parse a path string into parts, handling dot notation and array expressions
   * @param path The path string to parse
   * @returns An array of parsed path parts with key and isArray flag
   */
  function parsePath(path: string): Array<{ key: string; isArray: boolean }> {
    const parts: Array<{ key: string; isArray: boolean }> = [];

    // Split by dots, but handle array expressions
    let currentPart = '';
    let inArrayExpression = false;

    for (let i = 0; i < path.length; i++) {
      const char = path[i];

      if (char === '[') {
        // Start of array expression
        inArrayExpression = true;

        // Add the current part as a key
        if (currentPart) {
          parts.push({ key: currentPart, isArray: true });
          currentPart = '';
        }
      } else if (char === ']') {
        // End of array expression
        inArrayExpression = false;
      } else if (char === '.' && !inArrayExpression) {
        // Dot outside array expression - split the path
        if (currentPart) {
          parts.push({ key: currentPart, isArray: false });
          currentPart = '';
        }

        // Check if the next character is a digit (for .0. pattern)
        if (i + 1 < path.length && /^\d$/.test(path[i + 1])) {
          // Look ahead to find the end of the number
          let j = i + 1;
          while (j < path.length && /^\d$/.test(path[j])) {
            j++;
          }

          // If the number is followed by a dot, treat the previous part as an array
          if (j < path.length && path[j] === '.') {
            // Modify the last added part to be an array if there was one
            if (parts.length > 0) {
              parts[parts.length - 1].isArray = true;
            }

            // Skip the number and the following dot
            i = j;
          }
        }
      } else if (char !== '.' || inArrayExpression) {
        // Regular character or dot inside array expression
        currentPart += char;
      }
    }

    // Add the last part if there is one
    if (currentPart) {
      parts.push({ key: currentPart, isArray: false });
    }

    return parts;
  }

  return root;
};
